/**
 * StationManager — the orbital ring-station boss. TWO counter-rotating rings encircle
 * a rogue planet; each ring carries turrets, cannons, big cannons and hangar gates —
 * all Damageable, so the player shoots them off one by one. When every weapon on a
 * ring is destroyed, that ring STOPS spinning, darkens and burns. The two rings die
 * independently; the whole station is defeated once BOTH rings are dead.
 *
 * The data model owns each ring's rotation `angle`; the renderer draws the ring sprite
 * AND the weapon sprites at that same angle so they stay locked to the ring.
 */
import { Vector2 } from "@/math/Vector2";
import { STATION } from "@/config/balance";
import type { Damageable } from "@/game/combat/turrets";

export type StationWeaponKind = "turret" | "cannon" | "bigCannon" | "hangar";

/** One mounted weapon on a ring. Satisfies Damageable so player fire can hit it. */
export interface StationWeapon extends Damageable {
  kind: StationWeaponKind;
  ringIdx: number; // 0 = outer, 1 = inner
  lox: number; // local mount offset (ring-centre-relative texture px)
  loy: number;
  pos: Vector2; // world position, recomputed each frame from mount × scale × ring angle
  radius: number; // hit radius (world units)
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  angle: number; // aim angle (world) — set by the combat pass
  cooldown: number; // seconds until it can fire again
  flash: number; // muzzle flash timer
  dead: boolean;
  aggro: boolean; // always true while alive → the player's auto-cannons engage it
  burnT: number; // wreck-fire animation time once dead
  beam: number; // big-cannon: seconds of beam remaining
  beamAge: number;
  launchLeft: number; // hangar: craft still to spew this fight (fighters then a cruiser)
  launchTimer: number;
}

export interface StationRing {
  idx: number;
  scale: number; // texture px → world
  spinRate: number; // rad/s (signed: outer +CW, inner −CCW)
  angle: number; // current rotation
  dead: boolean; // all weapons destroyed → stopped + burning
  burnT: number; // ring-wide fire animation once dead
  weapons: StationWeapon[];
}

/** A red bolt fired by a station turret/cannon at the player. */
export interface StationBolt {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  dmg: number;
  big: boolean; // cannon bolt (bigger) vs turret bolt
}

/** A live big-cannon beam segment, rebuilt every frame for the renderer. */
export interface StationBeam {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  t: number; // 0 (just fired) → 1 (expiring)
}

/** Per-frame combat context the game hands the station. */
export interface StationCtx {
  target: { pos: { x: number; y: number } }; // the player mothership
  playerAlive: boolean;
  safe: boolean; // in a safe zone → hold fire
  damagePlayer: (dmg: number, x: number, y: number) => void;
  onHit: (x: number, y: number) => void; // impact spark VFX
}

/** Perpendicular distance from a point to the ray segment [o → o+dir·len] (dir unit). */
function distToSeg(
  px: number,
  py: number,
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  len: number,
): number {
  let t = (px - ox) * dx + (py - oy) * dy;
  t = t < 0 ? 0 : t > len ? len : t;
  return Math.hypot(px - (ox + dx * t), py - (oy + dy * t));
}

function makeWeapon(
  kind: StationWeaponKind,
  ringIdx: number,
  ox: number,
  oy: number,
  hp: number,
  shield: number,
  radius: number,
): StationWeapon {
  return {
    kind,
    ringIdx,
    lox: ox,
    loy: oy + STATION.mapCenterDy, // shift the mapper's image-centre oy onto the ring centre
    pos: new Vector2(0, 0),
    radius,
    hp,
    maxHp: hp,
    shield,
    maxShield: shield,
    shieldFlash: 0,
    hitTimer: 0,
    angle: 0,
    cooldown: Math.random() * 1.5,
    flash: 0,
    dead: false,
    aggro: true,
    burnT: 0,
    beam: 0,
    beamAge: 0,
    launchLeft: 0,
    launchTimer: 0,
  };
}

export class StationManager {
  readonly center = new Vector2(0, 0);
  readonly rings: StationRing[] = [];
  readonly bolts: StationBolt[] = []; // turret/cannon fire heading at the player
  readonly beams: StationBeam[] = []; // big-cannon beams (render descriptors, rebuilt each frame)
  private sparkT = 0;
  defeated = false;

  constructor(center: { x: number; y: number } = { x: 0, y: 0 }) {
    this.center.set(center.x, center.y);
    // Outer ring spins clockwise, inner ring counter-clockwise.
    this.rings.push(this.buildRing(0, STATION.outerScale, STATION.outerSpin));
    this.rings.push(this.buildRing(1, STATION.innerScale, -STATION.innerSpin));
  }

  private buildRing(idx: number, scale: number, spinRate: number): StationRing {
    const s = STATION;
    const weapons: StationWeapon[] = [];
    for (const m of s.mounts.turrets)
      weapons.push(makeWeapon("turret", idx, m.ox, m.oy, s.turretHp, 0, s.turretWorldSize * 0.7));
    for (const m of s.mounts.cannons)
      weapons.push(
        makeWeapon("cannon", idx, m.ox, m.oy, s.cannonHp, s.cannonShield, s.cannonWorldSize * 0.7),
      );
    for (const m of s.mounts.bigCannons)
      weapons.push(
        makeWeapon(
          "bigCannon",
          idx,
          m.ox,
          m.oy,
          s.bigCannonHp,
          s.bigCannonShield,
          s.bigCannonWorldSize * 0.7,
        ),
      );
    for (const m of s.mounts.gates) {
      const w = makeWeapon("hangar", idx, m.ox, m.oy, s.hangarHp, 0, s.hangarWorldSize * 0.7);
      w.launchLeft = s.hangarFighters + s.hangarCruisers; // spew fighters, then a cruiser last
      weapons.push(w);
    }
    return { idx, scale, spinRate, angle: 0, dead: false, burnT: 0, weapons };
  }

  /** Recompute every weapon's world position from its mount, the ring scale, and the
   * ring's current rotation (screen-space clockwise, matching the sprite). */
  private placeWeapons(ring: StationRing): void {
    const ca = Math.cos(ring.angle);
    const sa = Math.sin(ring.angle);
    for (const w of ring.weapons) {
      const sx = w.lox * ring.scale;
      const sy = w.loy * ring.scale;
      w.pos.set(this.center.x + (sx * ca - sy * sa), this.center.y + (sx * sa + sy * ca));
    }
  }

  /** Spin the live rings, aim + fire every gun in range at the player, advance bolts +
   * beams, and settle death state. Hangar launches are handled separately in GameState. */
  update(dt: number, ctx: StationCtx): void {
    this.beams.length = 0;
    this.sparkT -= dt;
    const spark = this.sparkT <= 0;
    if (spark) this.sparkT = 0.05;
    const s = STATION;
    const px = ctx.target.pos.x;
    const py = ctx.target.pos.y;

    for (const ring of this.rings) {
      if (!ring.dead) ring.angle += ring.spinRate * dt;
      else ring.burnT += dt;
      this.placeWeapons(ring);

      for (const w of ring.weapons) {
        w.flash = Math.max(0, w.flash - dt * 4);
        w.shieldFlash = Math.max(0, w.shieldFlash - dt * 3);
        w.hitTimer += dt;
        if (w.hp <= 0 && !w.dead) {
          w.dead = true;
          w.hp = 0;
          w.beam = 0;
        }
        if (w.dead) {
          w.burnT += dt;
          continue;
        }
        // Hangars don't shoot (they launch craft — driven by GameState).
        if (ctx.safe || !ctx.playerAlive || w.kind === "hangar") continue;

        const dx = px - w.pos.x;
        const dy = py - w.pos.y;
        const dist2 = dx * dx + dy * dy;
        const range =
          w.kind === "turret" ? s.turretRange : w.kind === "cannon" ? s.cannonRange : s.bigCannonRange;
        const inRange = dist2 <= range * range;

        // Swing the barrel toward the player while it's in this gun's own range.
        if (inRange) {
          let diff = Math.atan2(dy, dx) - w.angle;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          const step = s.aimTurnRate * dt;
          w.angle += diff > step ? step : diff < -step ? -step : diff;
        }
        w.cooldown -= dt;

        // Big cannon: sustain an active beam, damaging the player along its length.
        if (w.kind === "bigCannon" && w.beam > 0) {
          w.beam -= dt;
          w.beamAge += dt;
          const bx = Math.cos(w.angle);
          const by = Math.sin(w.angle);
          const len = Math.min(s.beamMaxLength, Math.sqrt(dist2) + 40);
          const x0 = w.pos.x + bx * w.radius;
          const y0 = w.pos.y + by * w.radius;
          this.beams.push({
            x0,
            y0,
            x1: x0 + bx * len,
            y1: y0 + by * len,
            t: 1 - w.beam / s.bigCannonBeamDuration,
          });
          if (
            ctx.playerAlive &&
            distToSeg(px, py, x0, y0, bx, by, len) < s.beamWidth + s.playerHitRadius
          ) {
            ctx.damagePlayer(s.bigCannonDps * dt, px, py);
            if (spark) ctx.onHit(px, py);
          }
          continue;
        }

        // Open fire once in range, lined up, and off cooldown.
        if (!inRange || w.cooldown > 0) continue;
        let post = Math.atan2(dy, dx) - w.angle;
        while (post > Math.PI) post -= Math.PI * 2;
        while (post < -Math.PI) post += Math.PI * 2;
        if (Math.abs(post) > s.fireCone) continue;
        if (w.kind === "bigCannon") {
          w.beam = s.bigCannonBeamDuration;
          w.beamAge = 0;
          w.cooldown = s.bigCannonFireInterval;
          w.flash = 1;
        } else {
          const bx = Math.cos(w.angle);
          const by = Math.sin(w.angle);
          this.bolts.push({
            x: w.pos.x + bx * w.radius,
            y: w.pos.y + by * w.radius,
            vx: bx * s.boltSpeed,
            vy: by * s.boltSpeed,
            life: s.boltLife,
            dmg: w.kind === "turret" ? s.turretDamage : s.cannonDamage,
            big: w.kind === "cannon",
          });
          w.cooldown = w.kind === "turret" ? s.turretFireInterval : s.cannonFireInterval;
          w.flash = 1;
        }
      }

      if (!ring.dead && ring.weapons.every((w) => w.dead)) ring.dead = true;
    }

    // Advance bolts → strike the player or fizzle.
    const hitR = s.playerHitRadius;
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let gone = b.life <= 0;
      if (!gone && ctx.playerAlive) {
        const ddx = b.x - px;
        const ddy = b.y - py;
        if (ddx * ddx + ddy * ddy < hitR * hitR) {
          ctx.damagePlayer(b.dmg, b.x, b.y);
          ctx.onHit(b.x, b.y);
          gone = true;
        }
      }
      if (gone) this.bolts.splice(i, 1);
    }

    this.defeated = this.rings.every((r) => r.dead);
  }

  /** Living weapons across both rings — Damageable targets for the player's guns. */
  liveWeapons(): StationWeapon[] {
    const out: StationWeapon[] = [];
    for (const ring of this.rings)
      for (const w of ring.weapons) if (!w.dead && w.hp > 0) out.push(w);
    return out;
  }
}
